import { getOrchestratorBaseUrl } from "./guestSession";
/** Scheduled-meeting invite lead-time contract (inclusive, in hours). */
export const DEFAULT_MEETING_LEAD_HOURS = 24;
export const MIN_MEETING_LEAD_HOURS = 0;
export const MAX_MEETING_LEAD_HOURS = 168;
/**
 * Parse the value used by the meeting form. Empty/null values use the API's
 * default, while malformed values return null so callers can show validation
 * feedback before submitting.
 */
export function parseMeetingLeadHours(value) {
    if (value === undefined || value === null || value === "")
        return DEFAULT_MEETING_LEAD_HOURS;
    if (typeof value !== "number" && typeof value !== "string")
        return null;
    const raw = typeof value === "string" ? value.trim() : value;
    if (raw === "")
        return DEFAULT_MEETING_LEAD_HOURS;
    const lead = Number(raw);
    return Number.isFinite(lead) && lead >= MIN_MEETING_LEAD_HOURS && lead <= MAX_MEETING_LEAD_HOURS
        ? lead
        : null;
}
async function jsonFetch(path, init) {
    const headers = new Headers(init?.headers);
    if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }
    const res = await fetch(`${getOrchestratorBaseUrl()}${path}`, {
        ...init,
        headers,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `HTTP ${res.status}`);
        err.code = data.error;
        err.details = data;
        throw err;
    }
    return data;
}
function hostSessionHeaders(deviceSessionToken) {
    return deviceSessionToken ? { Authorization: `Bearer ${deviceSessionToken}` } : undefined;
}
/** Fetch the raw HTML of the server's default invite template (it lives only
 *  on the server, the client cannot read it directly). Used by the pre-send
 *  preview; the client renders it with the same sample values as the storage
 *  templates. Throws with code "default_template_unavailable" when missing. */
export async function fetchDefaultInviteTemplate(emailLocale) {
    const data = await jsonFetch(`/magic-link/invite/default-template?emailLocale=${encodeURIComponent(emailLocale)}`);
    const responseLocale = data.emailLocale === "de" || data.emailLocale === "en"
        ? data.emailLocale
        : emailLocale;
    if (responseLocale !== emailLocale) {
        const error = new Error("default_template_locale_mismatch");
        error.code = "default_template_locale_mismatch";
        throw error;
    }
    // Older orchestrators return only { html } — treat a missing provider as
    // "emailjs" so the DEV warning errs on the side of showing up.
    return {
        html: String(data.html || ""),
        provider: data.provider === "smtp" ? "smtp" : "emailjs",
        emailLocale: responseLocale,
    };
}
/** True when the orchestrator rejected the call because the guest's invite
 *  only grants chat scope (403 { error: "insufficient_scope" }). */
export function isInsufficientScopeError(e) {
    return e?.code === "insufficient_scope";
}
export async function createInvites(params, deviceSessionToken) {
    return jsonFetch("/magic-link/invite", {
        method: "POST",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({
            ...params,
            appOrigin: window.location.origin,
            // The inviter's timezone so emailed times are not shown in UTC.
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
    });
}
/** The wallet's single active conference (or null when none is running). */
export async function getActiveConference(hostWallet, deviceSessionToken) {
    const data = await jsonFetch(`/magic-link/invites?hostWallet=${encodeURIComponent(hostWallet)}&appOrigin=${encodeURIComponent(window.location.origin)}`, { headers: hostSessionHeaders(deviceSessionToken) });
    return data.conference || null;
}
export async function revokeInvite(hostWallet, token, deviceSessionToken) {
    await jsonFetch("/magic-link/invite/revoke", {
        method: "POST",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({ hostWallet, token }),
    });
}
export async function deleteConference(hostWallet, deviceSessionToken) {
    return await jsonFetch("/magic-link/conference/delete", {
        method: "POST",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({
            hostWallet,
            // Cancellation mails show the meeting time in the organizer's zone.
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
    });
}
export async function verifyMagicLinkToken(token) {
    const data = await jsonFetch("/magic-link/verify", {
        method: "POST",
        body: JSON.stringify({ token }),
    });
    return {
        sessionJwt: data.sessionJwt,
        email: data.email,
        room: data.room,
        scope: data.scope === "full" ? "full" : "chat",
        validUntil: data.validUntil,
    };
}
export async function validateGuestSession(sessionJwt) {
    try {
        const data = await jsonFetch("/magic-link/session", {
            headers: { Authorization: `Bearer ${sessionJwt}` },
        });
        return data.valid === true;
    }
    catch {
        return false;
    }
}
export async function guestMpcCreate(sessionJwt) {
    return jsonFetch("/magic-link/mpc/create", {
        method: "POST",
        body: JSON.stringify({}),
        headers: { Authorization: `Bearer ${sessionJwt}` },
    });
}
export async function guestMpcRefresh(sessionJwt, clientId) {
    return jsonFetch("/magic-link/mpc/refresh", {
        method: "POST",
        body: JSON.stringify({ clientId }),
        headers: { Authorization: `Bearer ${sessionJwt}` },
    });
}
export async function listMeetings(hostWallet, deviceSessionToken) {
    const data = await jsonFetch(`/magic-link/meetings?hostWallet=${encodeURIComponent(hostWallet)}`, {
        headers: hostSessionHeaders(deviceSessionToken),
    });
    return data.meetings || [];
}
/** Must match the server (upload-orchestrator/src/routes/meetings.js). */
export function buildHostEmailAuthMessage(wallet, timestamp, email) {
    return `dwinity-meeting-host-email\n${wallet.toLowerCase()}\n${timestamp}\n${email}`;
}
/** One-time backfill: adopt the organizer's profile e-mail on the wallet's
 *  older 'scheduled' meetings that predate host_email (so the blocked-alert
 *  e-mail can reach them too). The alert destination is security-relevant,
 *  so the server requires a fresh personal_sign proof from the host wallet. */
export async function backfillMeetingHostEmail(hostWallet, hostEmail, signMessage, deviceSessionToken) {
    const timestamp = Date.now();
    const signature = await signMessage(buildHostEmailAuthMessage(hostWallet, timestamp, hostEmail));
    return jsonFetch("/magic-link/meetings/backfill-host-email", {
        method: "POST",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({ hostWallet, hostEmail, auth: { timestamp, signature } }),
    });
}
export async function deleteMeeting(hostWallet, id, deviceSessionToken) {
    return jsonFetch(`/magic-link/meetings/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({ hostWallet }),
    });
}
export async function createMeeting(params, deviceSessionToken) {
    const data = await jsonFetch("/magic-link/meetings", {
        method: "POST", headers: hostSessionHeaders(deviceSessionToken), body: meetingBody(params),
    });
    return { meeting: data.meeting, organizerEmail: data.organizerEmail ?? null };
}
function meetingBody(params) {
    return JSON.stringify({
        ...params,
        appOrigin: window.location.origin,
        timeZone: params.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
}
/** Explicit delivery for the selected occurrence; never called by rendering. */
export async function sendMeetingInvites(hostWallet, id, expectedValidFrom, deviceSessionToken) {
    return jsonFetch(`/magic-link/meetings/${encodeURIComponent(id)}/send`, {
        method: "POST",
        headers: hostSessionHeaders(deviceSessionToken),
        body: JSON.stringify({ hostWallet, expectedValidFrom }),
    });
}
export async function updateMeeting(id, params, deviceSessionToken) {
    const data = await jsonFetch(`/magic-link/meetings/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: hostSessionHeaders(deviceSessionToken),
        body: meetingBody(params),
    });
    return { meeting: data.meeting, liveUpdate: data.liveUpdate ?? null };
}
/** Canonical message signed for company-link administration. Keep this exact:
 * it is also the replay-protected domain separator used by the server. */
export function buildPermanentInviteAuthMessage(wallet, timestamp, action) {
    return `Dwinity permanent invite authorization\nroom: ${wallet.toLowerCase()}\naction: ${action}\nts: ${timestamp}`;
}
async function permanentInviteRequest(hostWallet, walletProvider, action) {
    const timestamp = Date.now();
    const message = buildPermanentInviteAuthMessage(hostWallet, timestamp, action);
    const hexMessage = "0x" +
        Array.from(new TextEncoder().encode(message))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
    const signature = (await walletProvider.request({
        method: "personal_sign",
        params: [hexMessage, hostWallet],
    }));
    return jsonFetch(`/magic-link/permanent-invite/${action}`, {
        method: "POST",
        body: JSON.stringify({
            hostWallet,
            auth: { timestamp, signature },
        }),
    });
}
export async function getPermanentInviteStatus(hostWallet, walletProvider) {
    return (await permanentInviteRequest(hostWallet, walletProvider, "status"));
}
export async function createPermanentInvite(hostWallet, walletProvider) {
    return (await permanentInviteRequest(hostWallet, walletProvider, "create"));
}
export async function rotatePermanentInvite(hostWallet, walletProvider) {
    return (await permanentInviteRequest(hostWallet, walletProvider, "rotate"));
}
export async function revokePermanentInvite(hostWallet, walletProvider) {
    return (await permanentInviteRequest(hostWallet, walletProvider, "revoke"));
}
